import { ArrowRight, CheckCircle, DownloadSimple, ImagesSquare, PaintBrushBroad, WarningCircle } from "@phosphor-icons/react";
import { AppLink } from "../components/Header";
import { track } from "../lib/analytics";
import { alternativeFaqs } from "../lib/seo";

export function RemoveBgAlternative() {
  return (
    <main className="alternative-main">
      <section className="alternative-hero">
        <div className="alternative-hero-copy">
          <p className="kicker">remove.bg transition guide</p>
          <h1>A practical remove.bg alternative</h1>
          <p>Keep the one-click workflow, transparent PNGs, and edge editing while remove.bg moves into Canva.</p>
          <div className="alternative-actions">
            <AppLink to="/#upload" className="primary-button" onClick={() => track("alternative_cta_opened", { location: "hero" })}>Remove a background <ArrowRight size={19} weight="bold" /></AppLink>
            <a className="secondary-button" href="#what-changed">See what changed</a>
          </div>
        </div>
        <figure className="alternative-visual">
          <img src="/assets/remove-bg-alternative-guide.webp" alt="Illustration of a coral running shoe before and after its background is removed" width="1600" height="1000" />
        </figure>
      </section>

      <section className="alternative-fact" id="what-changed">
        <div>
          <span>Checked September 3, 2026</span>
          <h2>What is actually changing?</h2>
        </div>
        <div className="alternative-fact-copy">
          <p><strong>remove.bg says its background-removal functionality is moving into Canva.</strong> Its API documentation separately says background removal moves to Leonardo.Ai on December 1, 2026.</p>
          <p>The accurate answer is that remove.bg is in transition. The website is not simply gone today, and browser users and API users have different next steps.</p>
          <div className="source-links">
            <a href="https://www.remove.bg/faq" target="_blank" rel="noreferrer">Official remove.bg announcement <ArrowRight size={16} /></a>
            <a href="https://www.leonardo.ai/news/removebg-api-leonardo" target="_blank" rel="noreferrer">Official API transition overview <ArrowRight size={16} /></a>
          </div>
        </div>
      </section>

      <section className="alternative-choices" aria-labelledby="choose-title">
        <header><h2 id="choose-title">Choose by the job.</h2><p>There is no universal best replacement. The shortest reliable workflow is usually the right one.</p></header>
        <div className="choice-grid">
          <article className="choice-primary">
            <ImagesSquare size={32} weight="fill" />
            <h3>You need one clean PNG</h3>
            <p>KleanBG keeps the familiar browser flow, then adds seller presets, batch queues, ZIP download, and simple edge refinement. No Canva account or purchased credit packs are required.</p>
          </article>
          <article>
            <PaintBrushBroad size={30} weight="fill" />
            <h3>You already design in Canva</h3>
            <p>Canva is the natural route when background removal is one step inside a larger design, presentation, or social workflow.</p>
            <a href="https://www.canva.com/features/background-remover/" target="_blank" rel="noreferrer">See Canva’s remover <ArrowRight size={17} /></a>
          </article>
          <article className="choice-api">
            <WarningCircle size={30} weight="fill" />
            <h3>You maintain an API integration</h3>
            <p>KleanBG is not a drop-in API replacement. Follow Leonardo.Ai’s official migration guide and test formats, limits, errors, and billing against your real workload.</p>
            <a href="https://www.leonardo.ai/news/removebg-api-leonardo" target="_blank" rel="noreferrer">Read the API guidance <ArrowRight size={17} /></a>
          </article>
        </div>
      </section>

      <section className="alternative-workflow" aria-labelledby="workflow-title">
        <header><h2 id="workflow-title">A three-minute switching check</h2><p>Test one easy image and one difficult image before changing your normal workflow.</p></header>
        <ol>
          <li><span>1</span><div><h3>Upload the original</h3><p>Use the same full-size PNG, JPEG, or WebP. Avoid comparing screenshots or previously compressed copies.</p></div></li>
          <li><span>2</span><div><h3>Inspect the difficult edges</h3><p>Zoom into hair, fur, laces, transparent objects, and narrow gaps. Use erase or restore when the automatic mask needs help.</p></div></li>
          <li><span>3</span><div><h3>Export and reopen the PNG</h3><p>Download the transparent PNG, reopen it over a dark and light background, and confirm its dimensions before using it in production.</p></div></li>
        </ol>
      </section>

      <section className="alternative-checklist" aria-labelledby="checklist-title">
        <div><DownloadSimple size={34} weight="fill" /><h2 id="checklist-title">What KleanBG covers</h2><p>A focused replacement for individual browser users, with honest limits.</p></div>
        <dl>
          <div><dt><CheckCircle size={19} weight="fill" /> Finished files</dt><dd>Export transparent, white, or marketplace-ready PNG, JPEG, and WebP files.</dd></div>
          <div><dt><CheckCircle size={19} weight="fill" /> Edge correction</dt><dd>Erase and restore brushes stay in the finishing editor.</dd></div>
          <div><dt><CheckCircle size={19} weight="fill" /> Seller presets</dt><dd>Apply Amazon, Etsy, Shopify, or eBay sizing to one image or a whole batch.</dd></div>
          <div><dt><CheckCircle size={19} weight="fill" /> Private processing</dt><dd>Images are streamed for the requested result, not kept in an image archive.</dd></div>
          <div><dt><WarningCircle size={19} weight="fill" /> Browser product</dt><dd>No batch desktop app, Photoshop plugin, or public developer API is promised.</dd></div>
        </dl>
      </section>

      <section className="alternative-faq" aria-labelledby="faq-title">
        <header><h2 id="faq-title">Questions about switching</h2><p>Short answers for people coming from remove.bg.</p></header>
        <div>{alternativeFaqs.map(({ question, answer }) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}</div>
      </section>

      <section className="alternative-sources">
        <h2>Sources and review</h2>
        <p>Reviewed by Koby Ofek on September 3, 2026. Product transitions change, so the official announcements remain the source of truth.</p>
        <div><a href="https://www.remove.bg/faq" target="_blank" rel="noreferrer">remove.bg migration FAQ</a><a href="https://www.remove.bg/api" target="_blank" rel="noreferrer">remove.bg API notice</a><a href="https://www.leonardo.ai/news/removebg-api-leonardo" target="_blank" rel="noreferrer">Leonardo.Ai transition overview</a><AppLink to="/contact">Contact KleanBG</AppLink></div>
      </section>
    </main>
  );
}
